import { useState } from 'react';
import { useQuery, usePaginatedQuery } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is a client library, not a backend module
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: standard convex _generated import
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated dataModel lives outside src/
import type { TCallDirection, TCallSentiment } from '@/@types/voiceAgents';

// ---------------------------------------------------------------------------
// Call History Filters
// ---------------------------------------------------------------------------

export function useCallHistoryFilters() {
  const [direction, setDirection] = useState<TCallDirection | undefined>(undefined);
  const [sentiment, setSentiment] = useState<TCallSentiment | undefined>(undefined);

  function clearFilters() {
    setDirection(undefined);
    setSentiment(undefined);
  }

  const hasActiveFilters = direction !== undefined || sentiment !== undefined;

  return { direction, setDirection, sentiment, setSentiment, clearFilters, hasActiveFilters };
}

// ---------------------------------------------------------------------------
// Call History (paginated)
// ---------------------------------------------------------------------------

export function useVoiceCallHistory(
  orgId: Id<'organisations'> | undefined,
  filters?: { direction?: TCallDirection; sentiment?: TCallSentiment }
) {
  const result = usePaginatedQuery(
    api.voiceCalls.queries.listByOrg,
    orgId !== undefined
      ? { orgId, direction: filters?.direction, sentiment: filters?.sentiment }
      : 'skip',
    { initialNumItems: 25 }
  );

  return {
    results: result.results,
    isLoading: result.status === 'LoadingFirstPage',
    loadMore: result.loadMore,
    status: result.status,
  };
}

// ---------------------------------------------------------------------------
// Call Detail
// ---------------------------------------------------------------------------

export function useVoiceCallDetail(callId: Id<'voiceCalls'> | undefined) {
  const data = useQuery(
    api.voiceCalls.queries.getCallWithDetails,
    callId !== undefined ? { callId } : 'skip'
  );
  return { data, isLoading: data === undefined };
}
